import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';

const VehicleCard = ({ vehicleData, isDesktop, widgetName, windowId }) => {
	const { showVin: showVinPref } = usePrefs();
	const showVin = showVinPref === 'true';
	const { pricing, title: titleArray, images, vin, link = '' } = vehicleData;
	const image = images ? images[0] : {};
	const title = titleArray[0] || '';
	const subTitle = titleArray[1] || '';

	const finalPrice = useMemo(() => {
		const foundPrice = pricing?.dprice?.find(
			(price) => price?.isFinalPrice
		);
		if (foundPrice) return foundPrice.value;
		return pricing?.retailPrice;
	}, [pricing]);

	const TitleTag = 'h3';

	const vehicleCardClasses = [
		'vehicle-card border mt-6',
		isDesktop ? 'vehicle-card--desktop' : 'w-100'
	];

	const vehicleTitleHeadingClasses = [
		'font-weight-normal',
		'm-0',
		'h4',
		'text-body'
	];

	const vehicleTitleSubHeadingClasses = [
		'ddc-font-size-xlarge',
		'display-5',
		'm-0',
		'h3',
		'letter-spacing-wide',
		'text-body'
	];

	const pricingClasses = [
		'font-weight-normal',
		'ddc-font-size-xlarge',
		'mt-2',
		'my-2',
		'text-body'
	];

	const vehicleVinClasses = [
		'vehicle-vin-label',
		'mb-2',
		'pb-1',
		'text-muted',
		'text-body',
		isDesktop ? 'ddc-font-size-xxsmall' : 'ddc-font-size-xsmall'
	];

	const handleClick = () => {
		trackEvent(widgetName, windowId, {
			result: 'User navigates to vehicle VDP',
			action: 'clicked',
			element: 'Vehicle Card'
		});
		window.location.href = link;
	};

	return (
		<div
			className={setClassNames(vehicleCardClasses)}
			data-testid="vehicle-card"
			onClick={() => handleClick()}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					handleClick();
				}
			}}
			role="link"
			tabIndex={0}
		>
			<img src={image?.uri || ''} alt={image?.alt || ''} />
			<div className="text-center border-top rounded p-3 d-flex flex-column">
				<a className="link" href={link} data-testid="card-link">
					<TitleTag className="d-flex flex-column m-0">
						<span
							className={setClassNames(
								vehicleTitleHeadingClasses
							)}
						>
							{title}
						</span>
						<span
							className={setClassNames(
								vehicleTitleSubHeadingClasses
							)}
							style={{
								wordWrap: 'break-word'
							}}
						>
							{subTitle}
						</span>
					</TitleTag>
					<span className={setClassNames(pricingClasses)}>
						{finalPrice}
					</span>
					{vin && showVin && (
						<div
							className={setClassNames(vehicleVinClasses)}
							data-testid="vin"
						>
							{`VIN: ${vin}`}
						</div>
					)}
				</a>
			</div>
		</div>
	);
};

VehicleCard.propTypes = {
	vehicleData: PropTypes.shape({
		title: PropTypes.arrayOf(PropTypes.string).isRequired,
		make: PropTypes.string.isRequired,
		vin: PropTypes.string,
		link: PropTypes.string,
		images: PropTypes.arrayOf(
			PropTypes.shape({
				uri: PropTypes.string.isRequired,
				alt: PropTypes.string.isRequired
			})
		).isRequired,
		pricing: PropTypes.shape({
			dprice: PropTypes.arrayOf(
				PropTypes.shape({
					label: PropTypes.string,
					type: PropTypes.string,
					value: PropTypes.number
				})
			),
			retailPrice: PropTypes.number,
			ePriceStatus: PropTypes.string
		}).isRequired
	}).isRequired,
	isDesktop: PropTypes.bool,
	widgetName: PropTypes.string,
	windowId: PropTypes.string
};

export default VehicleCard;
